import { useState } from "react";

import { applySetupCommand, newIdempotencyKey, setupErrorMessage } from "@/libs/tournamentSetupApi";
import type { SetupCommand, TournamentSetupView } from "@/libs/tournamentSetupApi";

type CommandBody = Omit<SetupCommand, "idempotency_key" | "expected_revision">;

/** Sends one setup command at the view's revision; the server answers with the
 * whole new view (or a structured error we show verbatim). */
export function useSetupCommand(view: TournamentSetupView, onViewChange: (next: TournamentSetupView) => void) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (command: CommandBody): Promise<boolean> => {
    setPending(true);
    setError(null);
    try {
      const next = await applySetupCommand(view.tournament_id, {
        ...command,
        idempotency_key: newIdempotencyKey(),
        expected_revision: view.revision,
      } as SetupCommand);
      onViewChange(next);
      return true;
    } catch (cause) {
      setError(setupErrorMessage(cause));
      return false;
    } finally {
      setPending(false);
    }
  };
  return { pending, error, run, clearError: () => setError(null) };
}
